import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, Clock, XCircle, ArrowRight, MessageSquare, Users, Video } from 'lucide-react';
// eslint-disable-next-line no-restricted-imports
import { apiClient } from '../../../shared/api/apiClient';

export default function ComponentUserSessions({ userId, jid, theme = 'dark' }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(!!userId || !!jid);

    const isLight = theme === 'light';
    const colors = {
        textPrimary: isLight ? '#111' : '#eee',
        textSecondary: isLight ? '#555' : '#aaa',
        textMuted: isLight ? '#888' : '#ccc',
        cardBg: isLight ? '#ffffff' : 'var(--bg-panel-darker)',
        cardBorder: isLight ? '1px solid #f0f0f0' : '1px solid rgba(255,255,255,0.1)',
        lineColor: isLight ? '#e5e7eb' : 'rgba(255,255,255,0.05)',
        iconBg: isLight ? '#f9fafb' : 'var(--bg-panel-dark)',
        iconBorder: isLight ? '2px solid #e5e7eb' : '2px solid rgba(255,255,255,0.1)',
        badgeBg: isLight ? '#f3f4f6' : 'rgba(255,255,255,0.15)',
        badgeText: isLight ? '#4b5563' : '#fff',
        avatarBorder: isLight ? '1px solid #e5e7eb' : '1px solid #444',
        avatarBg: isLight ? '#f3f4f6' : '#555',
        avatarText: isLight ? '#374151' : '#fff',
    };

    useEffect(() => {
        if (!userId && !jid) {
            return;
        }

        let mounted = true;
        const fetchSessions = async () => {
            try {
                const endpoint = userId 
                    ? `/api/booking/sessions/user/${userId}${jid ? `?jid=${encodeURIComponent(jid)}` : ''}`
                    : `/api/booking/sessions/contact/${encodeURIComponent(jid)}`;
                const res = await apiClient.get(endpoint);
                if (mounted && res.success) {
                    setSessions(res.sessions || []);
                }
            } catch (err) {
                console.error("Error fetching sessions", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchSessions();
        return () => { mounted = false; };
    }, [userId, jid]);

    if (loading) {
        return <div className="profile-readonly-text" style={{ color: colors.textSecondary }}>Cargando agenda de sesiones...</div>;
    }

    if (sessions.length === 0) {
        return <div className="profile-readonly-text" style={{ color: colors.textSecondary }}>No hay sesiones registradas para este usuario.</div>;
    }

    // Group by Date
    const groupedSessions = sessions.reduce((acc, session) => {
        const d = new Date(session.createdAt);
        const dateKey = d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(session);
        return acc;
    }, {});

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', position: 'relative' }}>
            {/* Timeline vertical line */}
            <div style={{
                position: 'absolute',
                top: '0',
                bottom: '0',
                left: '11px',
                width: '2px',
                background: colors.lineColor,
                zIndex: 0
            }} />

            {Object.entries(groupedSessions).map(([dateKey, dailySessions]) => (
                <div key={dateKey} style={{ display: 'flex', flexDirection: 'column', gap: '16px', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '24px', height: '24px', borderRadius: '50%', background: colors.iconBg,
                            border: colors.iconBorder, display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <Calendar size={12} color={colors.textSecondary} />
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: colors.textPrimary, textTransform: 'capitalize' }}>
                            {dateKey}
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingLeft: '32px' }}>
                        {dailySessions.map(session => {
                            let statusType = 'pending';
                            let statusLabel = 'PENDIENTE';
                            let icon = <Calendar size={14} />;
                            let color = '#3b82f6';
                            let bg = isLight ? '#e0f2fe' : 'rgba(59, 130, 246, 0.2)';

                            if (session.actualStartedAt) {
                                if (session.actualEndedAt) {
                                    statusType = 'finished';
                                    statusLabel = 'FINALIZADA';
                                    icon = <CheckCircle size={14} />;
                                    color = '#10b981';
                                    bg = isLight ? '#d1fae5' : 'rgba(16, 185, 129, 0.2)';
                                } else {
                                    statusType = 'active';
                                    statusLabel = 'EN CURSO';
                                    icon = <Video size={14} />;
                                    color = '#f59e0b';
                                    bg = isLight ? '#fef3c7' : 'rgba(245, 158, 11, 0.2)';
                                }
                            } else if (session.status === 'confirmed') {
                                statusType = 'confirmed';
                                statusLabel = 'CONFIRMADA';
                                icon = <CheckCircle size={14} />;
                                color = '#10b981';
                                bg = isLight ? '#d1fae5' : 'rgba(16, 185, 129, 0.2)';
                            } else if (session.status === 'expired') {
                                statusType = 'expired';
                                statusLabel = 'EXPIRADA';
                                icon = <XCircle size={14} />;
                                color = '#6b7280';
                                bg = isLight ? '#f3f4f6' : 'rgba(255,255,255,0.05)';
                            }

                            const durationLabel = session.actualDurationMinutes 
                                ? `${session.actualDurationMinutes} min (Real)`
                                : `${session.duration} min (Estimado)`;
                            
                            const participantCount = Math.max(session.activeParticipantsCount || 0, session.actualStartedAt ? 1 : 0);
                            
                            const usersToRender = [session.hostUser, session.targetUser].filter(Boolean);

                            return (
                                <div key={session.id} style={{ 
                                    border: colors.cardBorder, 
                                    borderRadius: '8px', 
                                    overflow: 'hidden',
                                    background: colors.cardBg,
                                    boxShadow: isLight ? '0 1px 2px rgba(0,0,0,0.02)' : 'none',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <div style={{
                                        padding: '10px 14px',
                                        background: bg,
                                        color: color,
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderBottom: colors.cardBorder
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {icon}
                                            {statusLabel}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ background: colors.badgeBg, color: colors.badgeText, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                                                {session.shortCode}
                                            </div>
                                            <span style={{ color: colors.textPrimary, fontSize: '13px' }}>Sesión Programada</span>
                                        </div>
                                    </div>
                                    
                                    <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textMuted }}>
                                            <ArrowRight size={14} color={colors.textSecondary} />
                                            <span><strong style={{ color: colors.textPrimary, fontWeight: '500' }}>Destino:</strong> {session.targetJid || 'Desconocido'}</span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginTop: '4px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: colors.textSecondary }}>
                                                <Clock size={14} color={colors.textSecondary} />
                                                <span>{durationLabel}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: colors.textSecondary }}>
                                                <MessageSquare size={14} color={colors.textSecondary} />
                                                <span>{session.messageCount || 0} mensajes</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: colors.textSecondary }}>
                                                <Users size={14} color={colors.textSecondary} />
                                                <span>{participantCount} {participantCount === 1 ? 'persona' : 'personas'}</span>
                                            </div>
                                        </div>

                                        {usersToRender.length > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '11px', color: colors.textSecondary, marginRight: '4px' }}>Usuarios internos:</span>
                                                {usersToRender.map(u => (
                                                    <div key={u.id} style={{ position: 'relative', width: '24px', height: '24px', flexShrink: 0 }} title={u.name}>
                                                        {u.image ? (
                                                            <img src={u.image} alt={u.name} style={{ width: '100%', height: '100%', borderRadius: '50%', border: colors.avatarBorder, objectFit: 'cover' }} />
                                                        ) : (
                                                            <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: colors.avatarBg, color: colors.avatarText, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', border: colors.avatarBorder }}>
                                                                {u.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                            <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                                                Creado a las {new Date(session.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                            {session.startTime && (
                                                <div style={{ background: colors.badgeBg, color: colors.textPrimary, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>
                                                    Programada: {new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
